// 魔法卡使用時機解析器
// Spell Timing Parser - 從效果文字解析使用時機

import type { SpellTiming, TimingParseResult } from '../../types/response';

/**
 * 時機關鍵字對應表
 */
const TIMING_PATTERNS: Array<{ pattern: RegExp; timing: SpellTiming; priority: number }> = [
  // 敵方階段相關（優先級最高）
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*敵方階段\s*\]?/,
    timing: 'opponent_phase',
    priority: 100,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*敵方使用魔法卡時\s*\]?/,
    timing: 'opponent_spell',
    priority: 100,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*敵方打出[/／]?召喚單位時\s*\]?/,
    timing: 'opponent_summon',
    priority: 100,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*敵方打出單位時\s*\]?/,
    timing: 'opponent_summon',
    priority: 100,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*敵方打出或召喚機械單位時\s*\]?/,
    timing: 'opponent_summon_mech',
    priority: 100,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*敵方防守階段結束時\s*\]?/,
    timing: 'defense_phase_end',
    priority: 90,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*敵方結束階段結束時\s*\]?/,
    timing: 'end_phase_end',
    priority: 90,
  },
  
  // 戰鬥/攻擊宣言/任意時機/回應
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*回應時\s*\]?/,
    timing: 'response',
    priority: 95,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*戰鬥時\s*\]?/,
    timing: 'combat',
    priority: 90,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*(?:攻擊宣言|進攻宣言)時\s*\]?/,
    timing: 'attack_declare',
    priority: 90,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*(?:任意時機|隨時)\s*\]?/,
    timing: 'any_time',
    priority: 80,
  },

  // 主要/防守階段（靈活型）
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*我方主要[/／]防守階段\s*\]?/,
    timing: 'main_or_defense',
    priority: 80,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*我方防守[/／]主要階段\s*\]?/,
    timing: 'main_or_defense',
    priority: 80,
  },
  
  // 僅防守階段
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*我方防守階段\s*\]?/,
    timing: 'defense_phase',
    priority: 70,
  },
  
  // 僅主要階段
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*我方主要階段開始時\s*\]?/,
    timing: 'main_phase_start',
    priority: 70,
  },
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*我方主要階段\s*\]?/,
    timing: 'main_phase',
    priority: 60,
  },
  
  // 戰術階段
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*我方戰術階段\s*\]?/,
    timing: 'tactics_phase',
    priority: 70,
  },
  
  // 特殊觸發
  {
    pattern: /\[?\s*使用時機\s*[:：]\s*你的單位偷襲成功時\s*\]?/,
    timing: 'sneak_success',
    priority: 90,
  },
  
  // 被動觸發（單位效果）
  {
    pattern: /敵方使用魔法卡時/,
    timing: 'opponent_spell',
    priority: 50,
  },
  {
    pattern: /敵方打出魔法卡時/,
    timing: 'opponent_spell',
    priority: 50,
  },
];

/**
 * 解析效果文字中的使用時機
 */
export function parseSpellTiming(effectText: string | null | undefined): TimingParseResult {
  if (!effectText) {
    return {
      timing: 'main_phase',
      rawText: '',
    };
  }

  let bestMatch: { timing: SpellTiming; priority: number } | null = null;
  const conditions: string[] = [];

  for (const { pattern, timing, priority } of TIMING_PATTERNS) {
    if (pattern.test(effectText)) {
      if (!bestMatch || priority > bestMatch.priority) {
        bestMatch = { timing, priority };
      }
    }
  }

  // 解析額外條件
  const conditionPatterns = [
    /若[^，,。]+[，,]/g,           // 若...條件
    /須在打出當下使用/g,           // 時機限制
  ];

  for (const pattern of conditionPatterns) {
    const matches = effectText.match(pattern);
    if (matches) {
      conditions.push(...matches);
    }
  }

  return {
    timing: bestMatch?.timing ?? 'main_phase',
    conditions: conditions.length > 0 ? conditions : undefined,
    rawText: effectText,
  };
}

/**
 * 檢查時機是否可以回應特定事件
 */
export function canRespondTo(
  timing: SpellTiming,
  triggerType: 'spell_cast' | 'unit_summon' | 'attack_declare' | 'attack_declaration' | 'unit_move' | 'effect_activate',
  isMyTurn: boolean
): boolean {
  // 任意時機可以回應所有事件
  if (timing === 'any_time') return true;
  // 回應時可以在任何連鎖中回應
  if (timing === 'response') return true;

  switch (triggerType) {
    case 'spell_cast':
      // 可以回應魔法卡的時機
      return timing === 'opponent_phase' ||
             timing === 'opponent_spell' ||
             (timing === 'main_or_defense' && !isMyTurn);

    case 'unit_summon':
      // 可以回應召喚的時機
      return timing === 'opponent_phase' ||
             timing === 'opponent_summon' ||
             timing === 'opponent_summon_mech';

    case 'attack_declare':
    case 'attack_declaration':
      // 可以回應進攻的時機（防守階段）
      return timing === 'defense_phase' ||
             timing === 'main_or_defense' ||
             timing === 'opponent_phase' ||
             timing === 'attack_declare' ||
             timing === 'combat';

    case 'unit_move':
      // 可以回應單位移動的時機
      return timing === 'opponent_phase' ||
             timing === 'defense_phase' ||
             timing === 'main_or_defense';

    case 'effect_activate':
      // 可以回應效果發動的時機
      return timing === 'opponent_phase' ||
             timing === 'opponent_spell';

    default:
      return false;
  }
}

/**
 * 檢查是否為回應型卡牌
 */
export function isResponseCard(timing: SpellTiming): boolean {
  return [
    'opponent_phase',
    'opponent_spell',
    'opponent_summon',
    'opponent_summon_mech',
    'combat',
    'attack_declare',
    'any_time',
    'response',
  ].includes(timing);
}

/**
 * 檢查是否可以在當前階段使用
 */
export function canUseInPhase(
  timing: SpellTiming,
  currentPhase: string,
  isMyTurn: boolean
): boolean {
  switch (timing) {
    case 'main_phase':
      return isMyTurn && currentPhase === 'summon';
      
    case 'defense_phase':
      return isMyTurn && currentPhase === 'defense';
      
    case 'main_or_defense':
      return isMyTurn && (currentPhase === 'summon' || currentPhase === 'defense');
      
    case 'tactics_phase':
      return isMyTurn && currentPhase === 'tactics';
      
    case 'opponent_phase':
      return !isMyTurn;
      
    case 'opponent_spell':
    case 'opponent_summon':
    case 'opponent_summon_mech':
    case 'attack_declare':
    case 'response':
      // 這些是觸發型，需要特定事件
      return false;
      
    case 'combat':
      // 戰鬥時可以在戰鬥相關階段使用
      return currentPhase === 'tactics' || currentPhase === 'defense';

    case 'any_time':
      // 任意時機可以隨時使用
      return true;

    default:
      return false;
  }
}

/**
 * 取得時機的中文描述
 */
export function getTimingDescription(timing: SpellTiming): string {
  const descriptions: Record<SpellTiming, string> = {
    main_phase: '我方主要階段',
    defense_phase: '我方防守階段',
    main_or_defense: '我方主要/防守階段',
    opponent_phase: '敵方階段',
    opponent_spell: '敵方使用魔法卡時',
    opponent_summon: '敵方打出/召喚單位時',
    opponent_summon_mech: '敵方打出/召喚機械單位時',
    defense_phase_end: '敵方防守階段結束時',
    end_phase_end: '敵方結束階段結束時',
    sneak_success: '你的單位偷襲成功時',
    main_phase_start: '我方主要階段開始時',
    tactics_phase: '我方戰術階段',
    combat: '戰鬥時',
    attack_declare: '攻擊宣言時',
    any_time: '任意時機',
    response: '回應時',
    special: '特殊時機',
  };
  
  return descriptions[timing] || '未知時機';
}
